import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Conversation,
  ConversationFilter,
  ConversationSort,
  ConversationStatus,
  PubsubChannelType,
} from 'sb-lib/types';
import { postAny } from 'shared/api';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  fetchConversation,
  fetchConversations,
  fetchConversationStats,
  fetchUnreadConversations,
  resetConversation,
  selectConversations,
} from 'shared/store/conversation-slice';
import { useLocalStorage } from 'shared/utils/local-storage';
import log from 'shared/utils/log';
import {
  useOrgSocketSubscription,
  useSocketSubscription,
  useUserSocketSubscription,
} from './pubsub';
import { useQueryParams } from './query';

type Conversations = Array<Conversation | { id: 0; fake: boolean }>;

const DEFAULT_CONVERSATION_FILTER = ConversationFilter.ALL;
const DEFAULT_CONVERSATION_STATUS = ConversationStatus.OPEN;
const DEFAULT_CONVERSATION_SORT = ConversationSort.NEWEST;

/**
 * Provide all necessary state and callbacks to manage a list of conversations.
 */
export function useConversationList(hoverPause?: boolean) {
  const hasEverConnected = useRef(false);
  const { isConnectedToServer } = useAppSelector(s => s.realtime);
  useEffect(() => {
    if (isConnectedToServer) {
      hasEverConnected.current = true;
    }
  }, [isConnectedToServer]);
  const [isBulkOpenClosing, setIsBulkOpenClosing] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState<
    Record<string, boolean>
  >({});
  const {
    conversationsFilter,
    conversationsStatus,
    conversationsSort,
    isFetchingConversations,
    hasMoreConversations,
    isFetchingMoreConversations,
    stats,
    unreadConversations,
    hasRecentUnseenUnassignedConversations,
    conversationsGroupId,
  } = useAppSelector(s => s.conversation);
  const dispatch = useDispatch();
  const [filter, setFilter] = useLocalStorage<ConversationFilter>({
    key: 'ConversationList:ConversationFilter',
    initialState: DEFAULT_CONVERSATION_FILTER,
  });
  const [status, setStatus] = useLocalStorage<ConversationStatus>({
    key: 'ConversationList:ConversationStatus',
    initialState: DEFAULT_CONVERSATION_STATUS,
  });
  const [sort, setSort] = useLocalStorage<ConversationSort>({
    key: 'ConversationList:ConversationSort',
    initialState: DEFAULT_CONVERSATION_SORT,
  });
  const [groupId, setGroupId] = useLocalStorage<number | null>({
    key: 'ConversationList:ConversationGroupIdFilter',
    initialState: null,
  });
  const conversations = useAppSelector(s => selectConversations(s, sort));
  const lastConversations = useRef<Conversations>([]);
  const { userGroups } = useAppSelector(s => s.auth);

  // Initial fetches
  useEffect(() => {
    dispatch(fetchUnreadConversations());

    if (!userGroups) {
      return;
    }

    dispatch(fetchConversationStats({ groupId: conversationsGroupId }));
  }, [dispatch, conversationsGroupId, userGroups]);

  // Fetch conversations whenever values change
  const shouldFetchConversations =
    filter !== conversationsFilter ||
    status !== conversationsStatus ||
    sort !== conversationsSort ||
    groupId !== conversationsGroupId;
  useEffect(() => {
    if (!shouldFetchConversations) return;
    dispatch(
      fetchConversations({
        page: 1,
        filter,
        status,
        sort,
        userGroupId: groupId || undefined,
      }),
    );
  }, [dispatch, shouldFetchConversations, filter, status, sort, groupId]);

  /**
   * Make various socket subscriptions.
   * We already subscribe to the All conversations to power Notifications
   * so no need to re-subscribe here, and definitely don't unsubscribe!
   */
  useOrgSocketSubscription(PubsubChannelType['chat.inbox.stats.org'], true, false);
  useUserSocketSubscription(PubsubChannelType['chat.inbox.stats.user'], true, false);
  useSocketSubscription(
    PubsubChannelType['chat.inbox.stats.group'],
    groupId ? groupId.toString() : undefined,
    true,
    true,
  );

  const conversationUpdatesChannel = `chat.inbox.conversation-updates.${status}.${filter}` as PubsubChannelType;
  const userFilters = [ConversationFilter.ASSIGNED, ConversationFilter.MENTIONED];
  const isUserChannelIdentifier = userFilters.includes(filter);
  const isGloballySubscribed =
    conversationUpdatesChannel ===
    PubsubChannelType['chat.inbox.conversation-updates.open.all'];

  useOrgSocketSubscription(
    conversationUpdatesChannel,
    !isUserChannelIdentifier && !isGloballySubscribed,
    !isGloballySubscribed,
  );
  useUserSocketSubscription(
    conversationUpdatesChannel,
    isUserChannelIdentifier && !isGloballySubscribed,
    !isGloballySubscribed,
  );

  /*** Reusable calculated data ***/
  const assignedUnreadCount = stats?.assigned?.openUnreadMessages || 0;
  const isLoading = isFetchingConversations || !stats;
  const allUnreadCount =
    (conversationsGroupId
      ? stats?.extraUsers?.reduce(
          (acc, user) => acc + user.stats.assigned.openUnreadMessages,
          0,
        )
      : stats?.all?.openUnreadMessages) || 0;

  const selectedConversationsIds = useMemo(
    () =>
      Object.entries(selectedConversations)
        .filter(([_, v]) => v === true)
        .map(([k]) => k),
    [selectedConversations],
  );

  const unreadConversationsList = useMemo(() => {
    const list = Object.entries(unreadConversations).map(([_, v]) => v);
    list.sort((a, b) => (a.dateLastMessage || 0) - (b.dateLastMessage || 0));
    return list;
  }, [unreadConversations]);

  const conversationsData = useMemo(() => {
    let c: Conversations = [];

    // Make fake cards for loading
    if (isLoading && conversations.length === 0) {
      const numCards = 7;
      for (let i = 0; i < numCards; i++) {
        c.push({ id: 0, fake: true });
      }
      lastConversations.current = [];
      return c;
    }

    if (
      hoverPause &&
      lastConversations.current.length > 0 &&
      lastConversations.current.length === conversations.length
    ) {
      // update conversation items, but not the shape of the list
      const cMap = conversations.reduce((a, c) => {
        a[c.id] = c;
        return a;
      }, {} as Record<string, Conversation>);
      return lastConversations.current.map(c => ({
        ...c,
        ...('fake' in c ? {} : cMap[c.id] || {}),
      }));
    }
    c = [...conversations];
    lastConversations.current = c;
    return c;
  }, [isLoading, hoverPause, conversations]);

  /*** Reusable callbacks ***/
  const loadMoreConversations = useCallback(async () => {
    const dateLastMessage = conversations[conversations.length - 1].dateLastMessage;
    if (isFetchingConversations || !dateLastMessage) return null;
    dispatch(fetchConversations({ page: 1, filter, status, sort, dateLastMessage }));
  }, [conversations, dispatch, filter, isFetchingConversations, sort, status]);

  const handleBulkOpenClose = useCallback(async () => {
    const action = status === ConversationStatus.OPEN ? 'close' : 'open';
    setIsBulkOpenClosing(true);
    try {
      // Close conversations in groups of at most 3 to avoid timeouts.
      const batchSize = 3;
      for (let i = 0; i < selectedConversationsIds.length; i += batchSize) {
        await postAny(`chat/conversations/${action}`, {
          conversationIds: selectedConversationsIds.slice(i, i + batchSize),
        });
      }
      setSelectedConversations({});
      message.success(
        `Conversation${selectedConversationsIds.length > 1 ? 's' : ''} ${
          action === 'open' ? 're-opened' : 'closed'
        }`,
      );
    } catch (err) {
      log.error('Failed to bulk close conversations', undefined, err);
      message.error(err.message || 'Failed to bulk close conversations');
    }
    setIsBulkOpenClosing(false);
  }, [selectedConversationsIds, status]);

  const handleBulkAssign = useCallback(
    async (assigneeId: number) => {
      try {
        await postAny(`chat/conversations/assign`, {
          conversationIds: selectedConversationsIds,
          assigneeId,
        });
        message.success(
          `Conversation${selectedConversationsIds.length > 1 ? 's' : ''} assigned`,
        );
        return selectedConversationsIds;
      } catch (err) {
        log.error('Failed to bulk assign conversations', undefined, err);
        throw err;
      }
    },
    [selectedConversationsIds],
  );

  return {
    hasMoreConversations,
    isFetchingMoreConversations,
    unreadConversations,
    selectedConversations,
    setSelectedConversations,
    filter,
    setFilter,
    status,
    setStatus,
    sort,
    setSort,
    groupId,
    setGroupId,
    assignedUnreadCount,
    allUnreadCount,
    isLoading,
    isBulkOpenClosing,
    selectedConversationsIds,
    unreadConversationsList,
    conversationsData,
    loadMoreConversations,
    handleBulkOpenClose,
    handleBulkAssign,
    hasRecentUnseenUnassignedConversations,
  };
}

/**
 * Sets up a watcher for the ?cid query param and fetches the conversation when set.
 */
export function useConversationIdQueryParam(visitorId: string | undefined) {
  const dispatch = useAppDispatch();
  const { params } = useQueryParams();
  const cid = params.get('cid') || '0';
  const conversationId = parseInt(cid, 10);

  useEffect(() => {
    if (visitorId) {
      dispatch(fetchConversation({ visitorId, conversationId }));
    }
    return () => {
      dispatch(resetConversation());
    };
  }, [conversationId, dispatch, visitorId]);
}
